import { useQuery } from '@tanstack/react-query'
import { createClient } from '@/lib/supabase/client'
import { FREE_TRIAL, type PricingTier } from '@/types/subscription'

/**
 * Hook to fetch pricing tiers from Stripe via edge function
 */
export function usePricing() {
  const supabase = createClient()

  const {
    data: paidTiers,
    isLoading,
    error,
  } = useQuery<PricingTier[]>({
    queryKey: ['pricing'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke<PricingTier[]>(
        'get-pricing'
      )
      if (error) throw error
      return data ?? []
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  })

  // Combine free trial with paid tiers
  const allTiers = [FREE_TRIAL, ...(paidTiers || [])]

  return {
    tiers: allTiers,
    paidTiers: paidTiers || [],
    isLoading,
    error,
  }
}

/**
 * Format price in cents to display string
 */
export function formatPrice(cents: number, interval: 'month' | 'year'): string {
  const dollars = cents / 100
  return `$${dollars}/${interval === 'month' ? 'mo' : 'yr'}`
}

/**
 * Format price for display with full interval name
 */
export function formatPriceDisplay(
  cents: number,
  interval: 'month' | 'year'
): { amount: string; interval: string } {
  const dollars = cents / 100
  return {
    amount: `$${dollars}`,
    interval: interval === 'month' ? 'month' : 'year',
  }
}
